// Types
import { User } from 'firebase/auth';
import {
  WorkPlace,
  ProjectMemberAdditionalRole,
  ProjectStatus,
  ActivityType,
} from '@shared/types';

// Constants
import {
  TimeOffResponseStatus,
  EmployeeTrainingStatus,
  EMPLOYEE_TRAINING_STATUS_LABEL,
  EMPLOYEE_TRAINING_STATUS_VARIANT,
  TRAINING_AVATAR_HIDDEN_TYPES,
  TRAINING_SUPPORTER_DISPLAY_TYPES,
  TRAINING_BADGE_DISPLAY_STATUSES,
  TrainingFieldIdType,
  EmployeeRelationshipRole,
} from '@/constants/employee';
import { PROJECT_MEMBER_ADDITIONAL_ROLE_LABEL } from '@/constants/project';
import {
  UserStatus,
  WORK_PLACES_TEXT,
  HALF_DAY_HOURS,
  HALF_DAY,
  FULL_DAY,
} from '@shared/constants';
import { INVALID_VALUE } from '@/constants/string';
import { PROJECT_DATE_STATUS } from '@/types/project';

// Utils
import { formatOrdinal, calculateWorkingHoursByDay } from '@shared/utils';
import { deepMerge, findValueByKeyMatch } from '@/utils/common';

// Types
import {
  EmployeeProject,
  EmployeeTraining,
  EmployeeLunchRegistration,
  EmployeeTrainingActivity,
  EmployeeActivity,
  TrainingTimelineActivity,
  EmployeeRelationship,
  EmployeeInfo,
  EmployeeProfile,
  EmployeeEducation,
} from '@/types/employee';
import { TrainingFieldIdOptions, TrainingFieldType } from '@/types/training';
import { BadgeVariant } from '@shared/ui';

export const getUserStatusColor = (workStatus: string) => {
  switch (workStatus) {
    case UserStatus.ACTIVE:
      return 'bg-green-500';
    case UserStatus.INACTIVE:
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
};

/**
 * Determine the overall time off request status
 * based on recipient responses.
 *
 * @param recipients - Array of recipient objects with a response status
 * @returns The aggregated request status:
 *          - `WAITING_FOR_APPROVE` if no recipients or no responses yet
 *          - `REJECTED` if any recipient rejected
 *          - `APPROVED` if at least one recipient approved and no rejections
 */
export const getTimeOffStatus = (
  recipients: Array<{ status: TimeOffResponseStatus }>,
): TimeOffResponseStatus => {
  if (!recipients.length) return TimeOffResponseStatus.WAITING_FOR_APPROVE;

  // Check if any recipient rejected
  const hasRejection = recipients.some(
    (recipient) => recipient.status === TimeOffResponseStatus.REJECTED,
  );

  if (hasRejection) return TimeOffResponseStatus.REJECTED;

  // Check if any recipient approved
  const hasApproval = recipients.some(
    (recipient) => recipient.status === TimeOffResponseStatus.APPROVED,
  );

  if (hasApproval) return TimeOffResponseStatus.APPROVED;

  // If no rejections and no approvals, still waiting
  return TimeOffResponseStatus.WAITING_FOR_APPROVE;
};

export const getWorkPlaceText = (value: string) => {
  const [workPlace, floor] = value.split('-');
  const floorFormatText = floor ? formatOrdinal(floor) : 'Unknown';

  switch (workPlace) {
    case WorkPlace.LH:
      return `${floorFormatText} floor, ${WORK_PLACES_TEXT[WorkPlace.LH]}`;
    case WorkPlace.NT:
      return `${floorFormatText} floor, ${WORK_PLACES_TEXT[WorkPlace.NT]}`;
    case WorkPlace.HPT:
      return `${floorFormatText} floor, ${WORK_PLACES_TEXT[WorkPlace.HPT]}`;
    case WorkPlace.TL18:
      return `${floorFormatText} floor, ${WORK_PLACES_TEXT[WorkPlace.TL18]}`;
    case WorkPlace.HOME:
      return WORK_PLACES_TEXT[WorkPlace.HOME];
    default:
      return 'Unknown';
  }
};

export const isLunchRegistration = (
  data: unknown,
): data is EmployeeLunchRegistration => {
  return typeof data === 'object' && data !== null && 'vegetarian' in data;
};

/**
 * Get badge properties for training status
 * @param status - Training status
 * @returns Object with variant and label for Badge component
 */
export const getTrainingBadgeProps = (
  status: EmployeeTrainingStatus,
): { variant: BadgeVariant; label: string } => {
  const variant = findValueByKeyMatch(
    status,
    EMPLOYEE_TRAINING_STATUS_VARIANT,
  ) as BadgeVariant;
  const label =
    findValueByKeyMatch(status, EMPLOYEE_TRAINING_STATUS_LABEL) ||
    INVALID_VALUE;

  return { variant, label };
};

/**
 * Check if the training status is active/ongoing
 * @param status - Training status
 * @returns True if the training is active (IDLE, IN_PROGRESS, RESUMED, or PAUSED), false otherwise
 */
export const isActiveTrainingStatus = (
  status: EmployeeTrainingStatus,
): boolean =>
  status === EmployeeTrainingStatus.IDLE ||
  status === EmployeeTrainingStatus.IN_PROGRESS ||
  status === EmployeeTrainingStatus.RESUMED ||
  status === EmployeeTrainingStatus.PAUSED;

/**
 * Check if a user is currently in training
 * @param trainings - Array of employee trainings
 * @returns True if the user has at least one training with IN_PROGRESS or RESUMED status, false otherwise
 */
export const isUserInTraining = (trainings: EmployeeTraining[]): boolean =>
  trainings.some(
    (training) =>
      !training.archived &&
      (training.status === EmployeeTrainingStatus.IN_PROGRESS ||
        training.status === EmployeeTrainingStatus.RESUMED),
  );

/**
 * Get the last training day from user's training history
 * @param trainings - Array of employee trainings
 * @returns The most recent date from completed training histories, or null if no completed trainings available
 */
export const getLastTrainingDay = (
  trainings: EmployeeTraining[],
): Date | null => {
  if (!trainings.length) return null;

  if (isUserInTraining(trainings)) {
    return null;
  }

  const completedDates: string[] = [];

  trainings.forEach((training) => {
    // Only consider history dates with 'completed' type
    if (training.histories?.length) {
      training.histories.forEach((history) => {
        if (
          history.time &&
          history.type !== EmployeeTrainingStatus.IN_PROGRESS &&
          history.type !== EmployeeTrainingStatus.IDLE
        ) {
          completedDates.push(history.time);
        }
      });
    }
  });

  if (!completedDates.length) return null;

  // Sort dates in descending order and get the most recent
  const sortedDates = completedDates
    .map((date) => new Date(date))
    .filter((date) => !isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime());

  const mostRecentDate = sortedDates[0];
  return mostRecentDate ? mostRecentDate : null;
};

/**
 * Get the latest completed day from training histories
 * @param histories - Array of training history objects
 * @returns The most recent date with COMPLETED status, or null if no completed histories available
 */
export const getLatestCompletedDay = (
  histories?: Array<{ type: EmployeeTrainingStatus; time: string }>,
): Date | null => {
  if (!histories?.length) return null;

  const completedHistories = histories.filter(
    (history) => history.type === EmployeeTrainingStatus.COMPLETED,
  );

  if (!completedHistories.length) return null;

  // Sort dates in descending order to get the most recent
  const sortedDates = completedHistories
    .map((history) => new Date(history.time))
    .filter((date) => !isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime());

  return sortedDates[0] || null;
};

export const sortTrainingsByStatusAndStartDate = (
  a: EmployeeTraining,
  b: EmployeeTraining,
) => {
  const aIsPaused = a.status === EmployeeTrainingStatus.PAUSED;
  const bIsPaused = b.status === EmployeeTrainingStatus.PAUSED;

  // If statuses are different, non-paused trainings come first
  if (aIsPaused !== bIsPaused) {
    return aIsPaused ? 1 : -1;
  }

  // Check if items have start date
  const aHasStartDate = !!a.start;
  const bHasStartDate = !!b.start;

  // Items without start date should go after items with start date
  if (aHasStartDate !== bHasStartDate) {
    return aHasStartDate ? -1 : 1;
  }

  // Sort by start date (newest first)
  const aStart = new Date(a.start || '').getTime();
  const bStart = new Date(b.start || '').getTime();

  return bStart - aStart;
};

export interface ExpandedItem {
  description?: boolean;
  feedback?: boolean;
}
/**
 * Get toggle states for training content sections
 * @param expandedContent - Current expanded content state
 * @returns Object with description and feedback toggle states as booleans
 */
export const getTrainingToggleStates = (expandedContent?: ExpandedItem) => ({
  feedback: !!expandedContent?.feedback,
  description: !!expandedContent?.description,
});

/**
 * Create activity dates from training histories
 * @param histories - Array of training histories
 * @returns Array of activity dates for DateStatusList
 */
export const createActivityDates = (
  histories: Array<{ time: string; type: string }> = [],
) =>
  histories.map(({ time, type }) => ({
    date: new Date(time),
    type,
  }));

/**
 * Format role with additional tag for display
 * @param role - The member role
 * @param tag - Optional additional role tag
 * @returns Formatted role string with tag if present
 */
export const formatRoleWithTag = (
  role: string,
  tag?: ProjectMemberAdditionalRole,
): string => {
  if (!tag) return role;

  const tagLabel = PROJECT_MEMBER_ADDITIONAL_ROLE_LABEL[tag] || tag;
  return `${role} and ${tagLabel} mode`;
};

/**
 * Get highlighted parts for text highlighting
 * @param name - Member name
 * @param role - Member role
 * @param project - Project name
 * @param tag - Optional additional role tag
 * @returns Array of parts to be highlighted
 */
export const getHighlightedParts = (
  name: string,
  role: string,
  project: string,
  tag?: ProjectMemberAdditionalRole,
): string[] => {
  const parts = [name, role, project];

  if (tag) {
    const tagLabel = PROJECT_MEMBER_ADDITIONAL_ROLE_LABEL[tag] || tag;
    parts.push(tagLabel);
  }

  return parts;
};

/**
 * Calculate total time off days based on working hours per day.
 * - Exactly 4 hours → counted as half-day (0.5)
 * - More than 4 hours → counted as full-day (1)
 * - Less than 4 hours → counted as 0
 * @param daysMap - Map<date, working hours>
 * @returns Rounded total days (nearest 0.5)
 */
const computeTimeOffDays = (daysMap: Map<string, number>): number => {
  const totalDays = Array.from(daysMap.values()).reduce((sum, hours) => {
    // Apply time-off rules per day
    if (hours < HALF_DAY_HOURS) return sum; // Less than 4 hours = 0 days
    if (hours === HALF_DAY_HOURS) return sum + HALF_DAY; // Exactly 4 hours = 0.5 days

    return sum + FULL_DAY; // More than 4 hours = 1 day
  }, 0);

  // Round to nearest 0.5
  const rounded = Math.round(totalDays * 2) / 2;

  // Return integer if it's a whole number (4.0 → 4)
  // Otherwise, keep decimal (4.5 → 4.5)
  return Number.isInteger(rounded) ? Math.trunc(rounded) : rounded;
};

/**
 * Calculate time off days for a single request.
 * @param start - Start date ISO string
 * @param end - End date ISO string
 * @returns Total time off days (rounded to nearest 0.5)
 */
export const calculateTimeOffDays = (
  start?: string | null,
  end?: string | null,
): number => {
  if (!start || !end) return 0;

  // Get working hours grouped by date
  const daysMap = calculateWorkingHoursByDay(start, end);

  return computeTimeOffDays(daysMap);
};

/**
 * Format time off days into a human-readable string
 * @param days - Total days as a number
 * @returns Formatted string in "X day(s)" format
 * @example
 * formatTimeOffDays(1) // returns "1 day"
 * formatTimeOffDays(2.5) // returns "2.5 days"
 * formatTimeOffDays(0) // returns ""
 */
export const formatTimeOffDays = (days: number): string => {
  if (days === 0) return '';

  const dayText = days === 1 ? 'day' : 'days';

  return `${days} ${dayText}`;
};

/**
 * Get the latest history type from project histories
 * @param histories - Array of project histories
 * @returns The type of the latest history entry, or undefined if no histories
 */
export const getLatestHistoryType = (
  histories?: Array<{ type: PROJECT_DATE_STATUS; time: Date }>,
): PROJECT_DATE_STATUS | undefined => {
  if (!histories?.length) return;

  const latestHistory = histories[histories.length - 1];

  return latestHistory?.type;
};

/**
 * Count the number of active projects for an employee
 * @param projects - Array of employee projects
 * @returns Number of active projects (non-archived, not paused, not completed, not left)
 */
export const countActiveProjects = (projects: EmployeeProject[]): number => {
  const inactiveProjectStatuses = [
    ProjectStatus.PAUSED,
    ProjectStatus.COMPLETED,
  ];
  const inactiveHistoryTypes = [
    PROJECT_DATE_STATUS.PAUSED,
    PROJECT_DATE_STATUS.COMPLETED,
    PROJECT_DATE_STATUS.LEFT,
  ];

  return projects.filter(({ archived, status, histories }) => {
    const latestType = getLatestHistoryType(histories);

    return (
      !archived &&
      !inactiveProjectStatuses.includes(status) &&
      (!latestType || !inactiveHistoryTypes.includes(latestType))
    );
  }).length;
};

/**
 * Sort employee projects with paused projects at the end and active projects sorted by newest first
 * @param projects - Array of normalized employee projects with histories
 * @returns Sorted array of projects
 *
 * Sorting logic:
 * 1. Non-paused projects appear first, sorted by latest history date (newest first)
 * 2. Paused projects appear at the end, also sorted by latest history date (newest first)
 */
export const sortProjectsByStatusAndDate = (
  projects: Array<
    Omit<EmployeeProject, 'histories'> & {
      userRoleColor: string;
      histories: Array<{ type: string; date: Date }>;
    }
  >,
) => {
  return projects.sort((a, b) => {
    const latestA = a.histories.at(-1);
    const latestB = b.histories.at(-1);

    const isPausedA =
      a.status === ProjectStatus.PAUSED ||
      latestA?.type === PROJECT_DATE_STATUS.PAUSED;
    const isPausedB =
      b.status === ProjectStatus.PAUSED ||
      latestB?.type === PROJECT_DATE_STATUS.PAUSED;

    // If one is paused and the other isn't, should go later
    if (isPausedA !== isPausedB) return isPausedA ? 1 : -1;

    // Newest projects first: compare latest history timestamps
    const timeA = latestA?.date?.getTime() ?? 0;
    const timeB = latestB?.date?.getTime() ?? 0;

    return timeB - timeA;
  });
};

/**
 * Updates trainings array by updating or adding activities to the appropriate trainings
 * @param trainings - Array of trainings
 * @param activityPayloads - The activity array to update or add
 * @returns Updated trainings array
 */
export const updateTrainingsWithActivity = (
  trainings: EmployeeTraining[],
  activityPayloads: (Omit<EmployeeActivity, 'data'> & {
    data: EmployeeTrainingActivity;
  })[],
): EmployeeTraining[] => {
  // Create a map of training IDs to their activities for efficient lookup
  const trainingActivityMap = new Map<
    string,
    (Omit<EmployeeActivity, 'data'> & { data: EmployeeTrainingActivity })[]
  >();

  activityPayloads.forEach((activity) => {
    const { training: trainingId } = activity.data;
    if (!trainingActivityMap.has(trainingId)) {
      trainingActivityMap.set(trainingId, []);
    }
    trainingActivityMap.get(trainingId)?.push(activity);
  });

  return trainings.map((training) => {
    const { id, activities } = training;
    const payloadsForTraining = trainingActivityMap.get(id);

    // If no payloads for this training, return unchanged
    if (!payloadsForTraining || payloadsForTraining.length === 0) {
      return training;
    }

    const updatedActivities = [...activities];

    // Process each activity payload for this training
    payloadsForTraining.forEach((activityPayload) => {
      const { id: activityId } = activityPayload;

      const existingActivityIndex = updatedActivities.findIndex(
        (activity) => activity.id === activityId,
      );

      if (existingActivityIndex !== -1) {
        const tempActivities = { ...updatedActivities[existingActivityIndex] };

        // Update existing activity in place
        updatedActivities[existingActivityIndex] = deepMerge(
          tempActivities,
          activityPayload,
        ) as Omit<EmployeeActivity, 'data'> & {
          data: EmployeeTrainingActivity;
        };
      } else {
        // Add new activity at the beginning
        updatedActivities.unshift(activityPayload);
      }
    });

    return {
      ...training,
      activities: updatedActivities,
    };
  });
};

/**
 * Check if a training topic is currently running
 * @param training - Training object with status property
 * @returns True if training is in-progress or resumed
 */
export const isTrainingRunning = (training: EmployeeTraining): boolean => {
  return training.status === 'in-progress' || training.status === 'resumed';
};

/**
 * Get the relevant date for a training item based on its status
 * For IDLE status, returns creation date (earliest date)
 * For all other statuses, returns most recent status date
 * @param training - Training object with status and histories
 * @returns Timestamp in milliseconds, or 0 if no histories exist
 */
export const getTrainingRelevantDate = (training: EmployeeTraining): number => {
  if (!training.histories || training.histories.length === 0) return 0;

  // For IDLE status, use creation date (earliest date)
  if (training.status === 'idle') {
    return Math.min(
      ...training.histories.map((h) => new Date(h.time).getTime()),
    );
  }

  // For all other statuses, use most recent status date
  return Math.max(...training.histories.map((h) => new Date(h.time).getTime()));
};

/**
 * Sort training items by archived status, running status, and relevant date
 * Non-archived running trainings come first, then non-archived non-running trainings,
 * and finally archived trainings at the end. Within each group, items are sorted by date (descending)
 * @param trainings - Array of training objects to sort
 * @returns Sorted array of trainings
 */
export const sortTrainingsByStatusAndDate = (
  trainings: EmployeeTraining[],
): EmployeeTraining[] => {
  return [...trainings].sort((a, b) => {
    const aIsArchived = a.archived ?? false;
    const bIsArchived = b.archived ?? false;

    // Archived trainings come last
    if (aIsArchived !== bIsArchived) {
      return aIsArchived ? 1 : -1;
    }

    const aIsRunning = isTrainingRunning(a);
    const bIsRunning = isTrainingRunning(b);

    // Running topics come first (within non-archived group)
    if (aIsRunning && !bIsRunning) return -1;
    if (!aIsRunning && bIsRunning) return 1;

    // Within same group, sort by relevant date (descending)
    return getTrainingRelevantDate(b) - getTrainingRelevantDate(a);
  });
};

/**
 * Check if a training status should display a badge
 * @param status - Training status to check
 * @returns True if status should display a badge
 */
export const shouldDisplayTrainingBadge = (
  status: EmployeeTrainingStatus,
): boolean => {
  return TRAINING_BADGE_DISPLAY_STATUSES.some((s) => s === status);
};

/**
 * Check if an activity type should hide avatar display
 * @param type - Activity type to check
 * @returns True if avatar should be hidden
 */
export const shouldHideActivityAvatar = (type: ActivityType): boolean => {
  return TRAINING_AVATAR_HIDDEN_TYPES.some((t) => t === type);
};

/**
 * Check if an activity type should display supporters grid
 * @param type - Activity type to check
 * @returns True if supporters grid should be displayed
 */
export const shouldDisplaySupporters = (type: ActivityType): boolean => {
  return TRAINING_SUPPORTER_DISPLAY_TYPES.some((t) => t === type);
};

/**
 * Generate unique field ID for training field edit tracking
 * @param trainingTopicId - Training topic ID
 * @param options - Field configuration options
 * @param options.type - Field type: 'description', 'presentation', 'feedback', 'create-feedback', or 'create-presentation'
 * @param options.activityId - Activity ID (required for presentation and feedback)
 * @returns Unique field ID string or undefined if required IDs are missing
 */
export const generateTrainingFieldId = (
  trainingTopicId: string,
  options: TrainingFieldIdOptions,
): string | undefined => {
  if (!trainingTopicId) return undefined;

  if (options.type === TrainingFieldIdType.DESCRIPTION) {
    return `desc-${trainingTopicId}`;
  }

  if (options.type === TrainingFieldIdType.CREATE_FEEDBACK) {
    return `create-feedback-${trainingTopicId}`;
  }

  if (options.type === TrainingFieldIdType.CREATE_PRESENTATION) {
    return `create-presentation-${trainingTopicId}`;
  }

  // For presentation and feedback, activityId is required
  if (!options.activityId) return undefined;

  const prefix =
    options.type === TrainingFieldIdType.PRESENTATION
      ? 'presentation'
      : 'feedback';

  return `${prefix}-${trainingTopicId}-${options.activityId}`;
};

/**
 * Type guard to check if an activity type is a supporter activity
 * @param type - Activity type to check
 * @returns True if the activity is a supporter assignment/unassignment
 */
export const isSupporterActivity = (
  type: ActivityType,
): type is
  | ActivityType.TRAINING_ASSIGN_SUPPORTER
  | ActivityType.TRAINING_UNASSIGN_SUPPORTER => {
  return TRAINING_SUPPORTER_DISPLAY_TYPES.some((t) => t === type);
};

/**
 * Convert a training activity to TimelineActivity format
 * @param activity - Activity to convert
 * @returns TimelineActivity object
 */
export const toTimelineActivity = <
  T extends Omit<EmployeeActivity, 'data'> & {
    data: EmployeeTrainingActivity;
  },
>(
  activity: T,
): TrainingTimelineActivity => {
  const { id, type, group, by, occurredAt, data } = activity;

  return {
    id,
    type,
    group,
    by,
    occurredAt,
    data,
  };
};

/**
 * Checks if a training topic has any feedback
 */
export const hasTrainingFeedback = (training: EmployeeTraining): boolean =>
  training.activities?.some(
    (activity) => activity.type === ActivityType.TRAINING_FEEDBACK_RECEIVED,
  ) ?? false;

/**
 * Checks if the current user has already provided feedback for a training topic
 * Each person can only provide one feedback per training topic
 */
export const hasUserProvidedFeedback = (
  training: EmployeeTraining,
  currentUserEmail?: string | null,
): boolean => {
  if (!currentUserEmail) return false;

  if (!Array.isArray(training.activities)) return false;

  return training.activities.some(
    ({ data }) => data?.related === currentUserEmail,
  );
};

/**
 * Get editor configurations for training description and feedback fields
 */
export const getTrainingEditorConfigs = (
  trainingTopicId: string,
  description: string,
) => ({
  description: {
    initialContent: description,
    options: {
      trainingTopicId,
      fieldType: ActivityType.TRAINING_UPDATE as TrainingFieldType,
      fieldId: generateTrainingFieldId(trainingTopicId, {
        type: TrainingFieldIdType.DESCRIPTION,
      }),
    },
  },
  feedback: {
    initialContent: '',
    options: {
      trainingTopicId,
      fieldType: ActivityType.TRAINING_FEEDBACK_RECEIVED as TrainingFieldType,
      isCreateMode: true,
    },
  },
});

export const getRelationshipOfUser = (
  currentUser?: User | null,
  identityRelationship?: EmployeeRelationship,
) => {
  const result: EmployeeRelationshipRole[] = [];

  if (!identityRelationship || !currentUser) return result;

  const { mentor, mentees, supportees, supporters } = identityRelationship;
  const { email = '' } = currentUser;

  if (mentor && mentor.email === email) {
    result.push(EmployeeRelationshipRole.MENTOR);
  }

  if (supporters?.some((supporter) => supporter.email === email)) {
    result.push(EmployeeRelationshipRole.SUPPORTER);
  }

  if (mentees?.some((mentee) => mentee.email === email)) {
    result.push(EmployeeRelationshipRole.MENTEE);
  }

  if (supportees?.some((supportee) => supportee.email === email)) {
    result.push(EmployeeRelationshipRole.SUPPORTEE);
  }

  return result;
};

/**
 * Checks if route is restricted (requires getEmployeeProfile instead of getEmployeeInfo)
 * @returns true if pathname matches any restricted route
 */
export const isRestrictedRoute = (
  pathname: string,
  restrictedRoutes: string[],
): boolean => {
  return (
    !!pathname &&
    !!restrictedRoutes?.length &&
    restrictedRoutes.some((route) => pathname.includes(route))
  );
};

/**
 * Maps EmployeeProfile to EmployeeInfo (limited fields for restricted routes)
 * Uses employeeId as fallback for missing or empty fields
 */
export const mapEmployeeProfileToEmployeeInfo = (
  profileData: EmployeeProfile | null,
  employeeId: string,
): EmployeeInfo => {
  if (!profileData) {
    return {
      id: employeeId,
      email: employeeId,
      name: employeeId,
      title: '',
    };
  }

  return {
    id: employeeId,
    email: profileData.email || employeeId,
    name: profileData.name || employeeId,
    title: profileData.title ?? '',
    username: profileData.username || employeeId,
    avatar: profileData.avatar,
  };
};

/**
 * Sort education items by graduation date (newest to oldest)
 * Items with graduation dates come first, sorted by newest graduation date.
 * Items without graduation dates are placed at the end.
 * @param educations - Array of education objects to sort
 * @returns Sorted array of educations
 */
export const sortEducationsByGraduation = (
  educations: EmployeeEducation[],
): EmployeeEducation[] => {
  return [...educations].sort((a, b) => {
    const aGraduation = a.graduation ? new Date(a.graduation).getTime() : 0;
    const bGraduation = b.graduation ? new Date(b.graduation).getTime() : 0;

    // Items without graduation dates go to the end
    if (aGraduation === 0 && bGraduation !== 0) return 1;
    if (aGraduation !== 0 && bGraduation === 0) return -1;

    // Sort by graduation date descending (newest first)
    return bGraduation - aGraduation;
  });
};

export const mergeActivitiesById = (
  existingActivities: EmployeeActivity[],
  incomingActivities: EmployeeTraining['activities'],
): EmployeeActivity[] => {
  if (!incomingActivities.length) return existingActivities;

  const existingIds = new Set(
    existingActivities.map((activity) => activity.id),
  );
  const uniqueIncoming = incomingActivities.filter(
    (activity) => !existingIds.has(activity.id),
  );

  if (!uniqueIncoming.length) return existingActivities;

  return [...uniqueIncoming, ...existingActivities];
};

export const isPastDate = (dateString: string): boolean => {
  return new Date(dateString) < new Date();
};

export const duplicateEmails = (
  currentSupporters: EmployeeProfile[],
  relationshipSupporters: EmployeeProfile[],
) =>
  new Set(
    currentSupporters
      .filter((s: EmployeeProfile) =>
        relationshipSupporters.some((rs) => rs.email === s.email),
      )
      .map((s: EmployeeProfile) => s.email),
  );
